import React, { useEffect, useRef } from "react";
import { FiTag, FiPlus } from "react-icons/fi";
import { useFormModal } from "@core/hooks/useFormModal";
import { FormModal, FormModalField } from "@core/components/FormModal";
import { useReportTagActions } from "@features/report/tag/hooks/useReportTagActions";
import { useScopeId } from "@core/hooks/useRouteParamId";
import {
    defaultDeadlineFromUntil,
    fromDatetimeLocalValue,
    isDeadlineOnOrAfterUntil,
} from "@features/report/tag/utils/reportTagDeadline";

type CreateTagForm = {
    name: string;
    description: string;
    since?: string;
    until?: string;
    deadline?: string;
};

const FIELDS: FormModalField<CreateTagForm>[] = [
    {
        name: "name",
        label: "Name",
        type: "text",
        required: true,
        placeholder: "Tag name",
        helpText: "A name for this report period, e.g. January 2025 or Q1 2025. Shown when selecting which period to report on.",
    },
    {
        name: "since",
        label: "Since",
        type: "date",
        required: true,
        placeholder: "Since",
        helpText: "First day of the reporting period this tag covers.",
    },
    {
        name: "until",
        label: "Until",
        type: "date",
        required: true,
        placeholder: "Until",
        helpText: "Last day of the reporting period this tag covers.",
    },
    {
        name: "deadline",
        label: "Deadline",
        type: "datetime-local",
        placeholder: "Deadline",
        helpText: "Optional last moment to submit without being late. Suggested from Until plus 14 days; clear to leave unset.",
    },
    {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Tag description",
        helpText: "Optional notes about this report period.",
    },
];

const CreateTagModal: React.FC<{
    show: boolean;
    onHide: () => void;
    nationId: number;
    onChanged?: () => void;
}> = ({ show, onHide, nationId, onChanged }) => {
    const scopeId = useScopeId();
    const resolvedNationId = nationId ?? scopeId ?? 0;
    const { doTagCreate } = useReportTagActions();
    const deadlineManuallySet = useRef(false);
    const prevUntil = useRef<string | undefined>(undefined);

    const { form, formErrors, busy, handleChange, setFieldValue, handleSubmit } =
        useFormModal<CreateTagForm>({
            initial: {
                name: "",
                description: "",
                since: undefined,
                until: undefined,
                deadline: undefined,
            },
            validate: (f) => {
                const errors: Partial<Record<keyof CreateTagForm, string[]>> = {};
                const messages: string[] = [];
                if (!f.name?.trim()) {
                    errors.name = ["Name is required."];
                    messages.push("Name is required.");
                }
                if (f.since === undefined || f.since === "") {
                    errors.since = ["Since date is required."];
                    messages.push("Since date is required.");
                }
                if (f.until === undefined || f.until === "") {
                    errors.until = ["Until date is required."];
                    messages.push("Until date is required.");
                }
                if (f.deadline && f.until && !isDeadlineOnOrAfterUntil(f.deadline, f.until)) {
                    errors.deadline = ["Deadline must be on or after the until date."];
                    messages.push("Deadline must be on or after the until date.");
                }
                return { errors, messages };
            },
            submit: (f) =>
                doTagCreate(resolvedNationId, {
                    name: f.name,
                    description: f.description,
                    since: f.since || null,
                    until: f.until || null,
                    deadline: fromDatetimeLocalValue(f.deadline),
                }),
            onSuccess: () => {
                onHide();
                onChanged?.();
            },
            errorFallback: "Failed to create tag",
        });

    useEffect(() => {
        if (!show) {
            deadlineManuallySet.current = false;
            prevUntil.current = undefined;
            return;
        }
    }, [show]);

    useEffect(() => {
        if (!show) return;
        const until = form.until;
        if (!until || until === prevUntil.current) {
            prevUntil.current = until;
            return;
        }
        prevUntil.current = until;
        if (deadlineManuallySet.current) return;
        setFieldValue("deadline", defaultDeadlineFromUntil(until));
    }, [show, form.until, setFieldValue]);

    const onFieldChange =
        (field: keyof CreateTagForm) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
            if (field === "deadline") {
                deadlineManuallySet.current = true;
            }
            handleChange(field)(e);
        };

    const onSetFieldValue = (field: keyof CreateTagForm, value: unknown) => {
        if (field === "deadline") {
            deadlineManuallySet.current = true;
        }
        setFieldValue(field, value);
    };

    return (
        <FormModal<CreateTagForm>
            show={show}
            onHide={onHide}
            title="Create New Report Tag"
            icon={<FiTag aria-hidden />}
            fields={FIELDS}
            form={form}
            formErrors={formErrors}
            busy={busy}
            onChange={onFieldChange}
            setFieldValue={onSetFieldValue}
            onSubmit={handleSubmit}
            submitLabel="Create Tag"
            busyLabel="Creating Tag…"
            submitIcon={<FiPlus className="me-2" />}
            testId="create-tag-modal"
        />
    );
};

export default CreateTagModal;
